"use client";

import { useEffect, useMemo, useRef, useState } from "react";

interface Point {
  label: string;
  value: number;
  raw?: string;
}

interface UsageChartProps {
  data: Point[];
  color?: string;
  height?: number;
  yTicks?: number[]; // 自定义 y 轴刻度，未传时自动
  formatY?: (v: number) => string;
  formatTip?: (p: Point) => string;
  xLabelEvery?: number; // 每隔几个点显示 x 轴标签
}

export function UsageChart({
  data,
  color = "#4D6BFE",
  height = 180,
  yTicks,
  formatY = (v) => String(v),
  formatTip = (p) => `${p.label} · ${p.value}`,
  xLabelEvery = 10,
}: UsageChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  const [drawing, setDrawing] = useState(false);

  const W = 720;
  const H = height;
  const PAD = { top: 12, right: 8, bottom: 26, left: 44 };

  const { ticks, path, areaPath, points, maxTick } = useMemo(() => {
    const maxV = Math.max(...data.map((d) => d.value), 1);
    const niceMax = niceCeil(maxV);
    const t =
      yTicks ??
      (() => {
        const n = 3;
        return Array.from({ length: n + 1 }, (_, i) => (niceMax / n) * i);
      })();
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    const x = (i: number) => PAD.left + (i / Math.max(data.length - 1, 1)) * innerW;
    const y = (v: number) => PAD.top + innerH - (v / Math.max(...t, 1)) * innerH;

    const pts = data.map((d, i) => ({ x: x(i), y: y(d.value), ...d }));

    const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    const area = `${line} L${pts[pts.length - 1].x.toFixed(1)},${(PAD.top + innerH).toFixed(1)} L${pts[0].x.toFixed(1)},${(PAD.top + innerH).toFixed(1)} Z`;

    return { ticks: t, path: line, areaPath: area, points: pts, maxTick: Math.max(...t, 1) };
  }, [data, yTicks, H]);

  // 数据变化（或首次挂载）时重播线条生长动画
  useEffect(() => {
    setDrawing(false);
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setDrawing(true));
    });
    return () => cancelAnimationFrame(id);
  }, [data]);

  const indexAtX = (clientX: number): number | null => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const px = ((clientX - rect.left) / rect.width) * W;
    const idx = Math.round(((px - PAD.left) / (W - PAD.left - PAD.right)) * (data.length - 1));
    return Math.max(0, Math.min(data.length - 1, idx));
  };

  const onMove = (e: React.MouseEvent) => {
    const idx = indexAtX(e.clientX);
    if (idx !== null) setHover(idx);
  };

  const onClick = (e: React.MouseEvent) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.round(((px - PAD.left) / (W - PAD.left - PAD.right)) * (data.length - 1));
    const clamped = Math.max(0, Math.min(data.length - 1, idx));
    // 点中数据点 → 固定高亮（再点一次取消）；点空白 → 取消
    if (Math.abs(points[clamped].x - px) <= 26) {
      setPinned((prev) => (prev === clamped ? null : clamped));
    } else {
      setPinned(null);
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const idx = indexAtX(e.touches[0].clientX);
    if (idx !== null) setHover(idx);
  };
  const onTouchEnd = () => {
    if (hover !== null) setPinned(hover);
  };

  const moveBy = (delta: number) => {
    setPinned((prev) => {
      const base = prev ?? hover ?? 0;
      const next = Math.max(0, Math.min(data.length - 1, base + delta));
      setHover(next);
      return next;
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      moveBy(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      moveBy(-1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (hover !== null) setPinned((prev) => (prev === hover ? null : hover));
    }
  };

  if (data.length === 0) return null;

  // 鼠标在图表内时跟随 hover；移出后停留 pinned
  const showIndex = hover ?? pinned;
  const showPt = showIndex !== null ? points[showIndex] : null;

  return (
    <div className="relative w-full">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        tabIndex={0}
        className="w-full select-none outline-none"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        onClick={onClick}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onKeyDown={onKeyDown}
        role="img"
        aria-label={`用量图表，共 ${data.length} 个数据点，方向键左右可逐点移动，回车或空格固定/取消高亮`}
      >
        {/* grid + y labels */}
        {ticks.map((t, i) => {
          const y =
            PAD.top +
            (H - PAD.top - PAD.bottom) -
            (t / maxTick) * (H - PAD.top - PAD.bottom);
          return (
            <g key={i}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} stroke="#eef0f2" strokeWidth={1} />
              <text x={PAD.left - 6} y={y + 3.5} textAnchor="end" fontSize={11} fill="#7f8287">
                {formatY(t)}
              </text>
            </g>
          );
        })}

        {/* x labels */}
        {data.map((d, i) =>
          i % xLabelEvery === 0 || i === data.length - 1 ? (
            <text
              key={i}
              x={
                points[i]?.x ??
                PAD.left + (i / Math.max(data.length - 1, 1)) * (W - PAD.left - PAD.right)
              }
              y={H - 6}
              textAnchor="middle"
              fontSize={11}
              fill="#7f8287"
            >
              {d.label}
            </text>
          ) : null
        )}

        {/* area (fade-in) */}
        <path
          d={areaPath}
          fill={color}
          opacity={drawing ? 0.12 : 0}
          stroke="none"
          style={{ transition: "opacity 0.9s ease" }}
        />
        {/* line (grow-in) */}
        <path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          pathLength={1}
          style={{
            strokeDasharray: 1,
            strokeDashoffset: drawing ? 0 : 1,
            transition: "stroke-dashoffset 0.9s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />

        {/* crosshair */}
        {showIndex !== null && (
          <g pointerEvents="none">
            <line
              x1={showPt!.x}
              y1={PAD.top}
              x2={showPt!.x}
              y2={H - PAD.bottom}
              stroke={color}
              strokeWidth={1}
              strokeDasharray="4 4"
              opacity={0.7}
            />
            <line
              x1={PAD.left}
              y1={showPt!.y}
              x2={W - PAD.right}
              y2={showPt!.y}
              stroke={color}
              strokeWidth={1}
              strokeDasharray="4 4"
              opacity={0.5}
            />
          </g>
        )}

        {/* hover: glow + dot */}
        {hover !== null && (
          <g pointerEvents="none">
            <circle cx={points[hover].x} cy={points[hover].y} r={10} fill={color} opacity={0.18} />
            <circle cx={points[hover].x} cy={points[hover].y} r={4} fill={color} stroke="#fff" strokeWidth={2} />
          </g>
        )}

        {/* pinned: solid dot + value label */}
        {pinned !== null && (
          <g pointerEvents="none">
            <circle cx={points[pinned].x} cy={points[pinned].y} r={6} fill={color} stroke="#fff" strokeWidth={2.5} />
            <text
              x={Math.max(PAD.left + 30, Math.min(W - PAD.right - 30, points[pinned].x))}
              y={Math.max(14, points[pinned].y - 12)}
              textAnchor="middle"
              fontSize={11}
              fontWeight={600}
              fill={color}
            >
              {formatTip(data[pinned])}
            </text>
          </g>
        )}
      </svg>

      {/* tooltip (fixed to the point, doesn't jitter) */}
      {showIndex !== null && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-md bg-[#212327] px-2.5 py-1.5 text-xs text-white shadow-lg"
          style={{
            left: `${Math.max(6, Math.min(94, (showPt!.x / W) * 100))}%`,
            top: `${Math.max(3, Math.min(76, (showPt!.y / H) * 100 - 4))}%`,
          }}
        >
          <div className="whitespace-nowrap tabular-nums">{formatTip(data[showIndex])}</div>
          <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-4 border-transparent border-t-[#212327]" />
        </div>
      )}
    </div>
  );
}

function niceCeil(v: number): number {
  if (v <= 0) return 4;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return nice * pow;
}
